import { useState, useRef } from "react";
import { saveAs } from "file-saver";
import * as pdfjsLib from "pdfjs-dist";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { useTheme } from "../../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faFileWord, 
  faCloudUploadAlt, 
  faTimes,
  faFileDownload,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";

const PdfToWordConverter = () => {
    const { darkMode } = useTheme();
    const [pdfFile, setPdfFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [isConverting, setIsConverting] = useState(false);
    const [error, setError] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const uploadAreaRef = useRef(null);

    // Initialize PDF.js worker
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    const handleFileUpload = (file) => {
        setError("");
        if (!file) return;

        if (file.type !== "application/pdf") {
            setError("Please upload a valid PDF file.");
            return;
        }

        setPdfFile(file);
        setFileName(file.name.replace(/\.[^/.]+$/, "")); // Remove extension
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFileUpload(e.dataTransfer.files[0]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleInputChange = (e) => {
        handleFileUpload(e.target.files[0]);
        e.target.value = ''; // Reset input to allow selecting same file again
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const clearSelectedFile = () => {
        setPdfFile(null);
        setFileName("");
        setError("");
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const convertPdfToWord = async () => {
        if (!pdfFile) {
            setError("Please upload a PDF file first.");
            return;
        }

        setIsConverting(true);
        setError("");

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const loadingTask = pdfjsLib.getDocument(arrayBuffer);
            const pdf = await loadingTask.promise;

            let fullText = "";

            // Extract text from each page
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                // Joining items with a space, then add a newline after each page for better separation
                const pageText = textContent.items
                    .map((item) => item.str)
                    .join(" ");
                fullText += pageText + "\n\n";
            }

            // Create Word document content - each paragraph in its own div
            const paragraphs = fullText.split("\n").filter(p => p.trim() !== '');
            const docBodyContent = paragraphs.map(p => `<div>${p}</div>`).join('');

            const docxContent = `
                <html xmlns:o='urn:schemas-microsoft-com:office:office' 
                      xmlns:w='urn:schemas-microsoft-com:office:word' 
                      xmlns='http://www.w3.org/TR/REC-html40'>
                  <head>
                    <title>${fileName}</title>
                    <meta charset="UTF-8">
                    <!--[if gte mso 9]>
                    <xml>
                      <w:WordDocument>
                        <w:View>Print</w:View>
                        <w:Zoom>100</w:Zoom>
                        <w:DoNotOptimizeForBrowser/>
                      </w:WordDocument>
                    </xml>
                    <![endif]-->
                  </head>
                  <body>
                    ${docBodyContent}
                  </body>
                </html>
            `;

            // Create Blob and download
            const blob = new Blob([docxContent], {
                type: "application/msword",
            });
            saveAs(blob, `${fileName}.doc`);

            setError("Conversion complete! Please note: This tool extracts text. Complex layouts and images may not be preserved.");

        } catch (err) {
            console.error("Conversion error:", err);
            setError(`Error converting PDF to Word: ${err.message || 'An unknown error occurred.'} Please ensure it's a valid PDF and try again. Note: This tool primarily extracts text, formatting may vary.`);
        } finally {
            setIsConverting(false);
        }
    };

    return (
        <>
            <HeaderSection toolId="pdfToWord" />
            <div className={`max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className={`bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 ${
                    darkMode 
                        ? 'bg-gray-800 border-gray-700 shadow-gray-900/10' 
                        : 'bg-white border-gray-200'
                }`}>
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon icon={faFileWord} className="mr-3" />
                            PDF to Word Converter
                        </h1>
                        <p className="opacity-90">
                            Convert PDF documents to editable Word files
                        </p>
                    </div>

                    {/* Upload Area */}
                    <section 
                        ref={uploadAreaRef}
                        className={`border-2 border-dashed rounded-xl py-10 px-4 md:px-20 m-6 text-center cursor-pointer transition-all duration-300 ${
                            isDragging 
                                ? 'border-green-500 bg-green-100 dark:bg-green-900/30' 
                                : fileName 
                                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                                    : 'border-gray-300 hover:border-green-400 hover:bg-green-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={triggerFileInput}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <FontAwesomeIcon 
                            icon={faCloudUploadAlt} 
                            className={`text-4xl md:text-5xl mb-4 p-6 transition-transform duration-300 hover:scale-110 ${
                                darkMode ? 'text-green-400' : 'text-green-500'
                            }`} 
                        />
                        <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
                            Drag & drop your PDF file here or click to browse
                        </p>
                        <input 
                            type="file" 
                            ref={fileInputRef}
                            className="hidden" 
                            accept=".pdf,application/pdf"
                            onChange={handleInputChange}
                        />
                        {fileName && (
                            <div className="mt-4 text-sm font-medium flex items-center justify-center gap-2">
                                <span className={darkMode ? 'text-gray-200' : 'text-green-600'}>{fileName}.pdf</span>
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearSelectedFile();
                                    }}
                                    className={`transition-colors duration-300 ${
                                        darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        )}
                    </section>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 m-6">
                        <button
                            onClick={convertPdfToWord}
                            disabled={!pdfFile || isConverting}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                                pdfFile 
                                    ? darkMode
                                        ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                                        : 'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'
                                    : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-70 text-gray-600 dark:text-gray-300'
                            }`}
                        >
                            {isConverting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    Converting...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faFileDownload} />
                                    Convert to Word
                                </>
                            )}
                        </button>
                        {pdfFile && (
                            <button
                                onClick={clearSelectedFile}
                                className={`py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                                    darkMode
                                        ? 'border border-green-400 text-green-400 hover:bg-gray-700'
                                        : 'border border-green-500 text-green-600 hover:bg-green-50'
                                }`}
                            >
                                Clear File
                            </button>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className={`m-6 p-4 rounded-lg border-l-4 ${
                            darkMode 
                                ? 'bg-red-900/30 border-red-500' 
                                : 'bg-red-50 border-red-500'
                        }`}>
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg
                                        className="h-5 w-5 text-red-500"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className={`text-sm ${
                                        darkMode ? 'text-red-300' : 'text-red-700'
                                    }`}>
                                        {error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <BottomSection toolId="pdfToWord" />
        </>
    );
};

export default PdfToWordConverter;